import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { OrderDetails, Product } from "../types/restaurant";
import { notifyError } from "src/assets/toastService";

interface UseEditOrderHandlerProps {
  products: Product[];
  setSelectedProducts: (products: Product[]) => void;
  setDiscount: (discount: number) => void;
  setIsEditing: (editing: boolean) => void;
  setCustomerName: (name: string) => void;
  setPhoneNumber: (phone: string) => void;
  setTableNumber: (table: string) => void;
  setGuess: (guess: string) => void;
  setOrderType: (type: string) => void;
  setDeliveryFee: (fee: number) => void;
}

export const useEditOrderHandler = ({
  products,
  setSelectedProducts,
  setDiscount,
  setIsEditing,
  setCustomerName,
  setPhoneNumber,
  setTableNumber,
  setGuess,
  setOrderType,
  setDeliveryFee
}: UseEditOrderHandlerProps) => {
  const location = useLocation();
  const [oldProducts, setOldProducts] = useState<OrderDetails>();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  useEffect(() => {
    const handleEditOrder = async () => {
      if (location.state?.editOrder) {
        setIsLoadingOrder(true);

        try {
          const editOrder = location.state.editOrder;
          
          // First, clear the location state to prevent re-running on refresh
          window.history.replaceState({}, document.title);
          
          setOldProducts(editOrder);
          setIsEditing(true);

          // Clear current selected products
          setSelectedProducts([]);

          // Pre-fill customer information
          if (editOrder.CustomerName) {
            setCustomerName(editOrder.CustomerName);
          }
          if (editOrder.phone_number) {
            setPhoneNumber(editOrder.phone_number);
          }
          if (editOrder.table_number) {
            setTableNumber(editOrder.table_number);
          }
          if (editOrder.guess) {
            setGuess(editOrder.guess);
          }
          // Order type & delivery fee
    if (editOrder.OrderType) setOrderType(editOrder.OrderType);
    if (editOrder.deliveryFee !== undefined && editOrder.deliveryFee !== null) {
      setDeliveryFee(Number(editOrder.deliveryFee));
    } else {
      setDeliveryFee(0);
    }

          // Pre-fill products - handle different product structures
          if (editOrder.Products && editOrder.Products.length > 0) {
            const selectedProductsFromOrder = editOrder.Products.map((item: any) => {
              
              // Get product ID from multiple possible locations
              const productId = item._id?.$oid || item.productId || item._id;
              
              if (!productId) {
                console.warn("⚠️ Product item missing ID:", item);
                return null;
              }

              // Try to find the product in your products list
              const filterProduct = products.find((product) => {
                const prodId = product._id?.$oid
                return prodId === productId;
              });

              if (filterProduct) {
                return {
                  ...filterProduct,
                  quantity: item.quantity || 1,
                  notes: item.notes || item.note || "",
                  totalAmount: Number(filterProduct.Amount || 0) * (item.quantity || 1),
                };
              } else {
                return {
                  _id: { $oid: productId },
                  ProductName: item.productName || "Unknown Product",
                  ArabicName: item.arabicword || "",
                  Amount: item.amount?.toString() || "0",
                  quantity: item.quantity || 1,
                  notes: item.notes || item.note || "",
                  Type: item.Type || "",
                } as Product;
              }
            }).filter(Boolean) as Product[];
            setSelectedProducts(selectedProductsFromOrder);
          } else {
            console.error("No products in edit order");
          }

          // Pre-fill discount
          if (editOrder.discount !== undefined && editOrder.discount !== null) {
            const discountValue = parseFloat(String(editOrder.discount)) || 0;
            setDiscount(discountValue);
          } else {
            setDiscount(0);
          }
        } catch (error) {
          console.error("Error loading edit order:", error);
          notifyError("Failed to load order for editing");
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    handleEditOrder();
  }, [location.state, products, setSelectedProducts, setDiscount, setIsEditing, setCustomerName, setPhoneNumber, setTableNumber, setGuess, setOrderType, setDeliveryFee,]);

  return { oldProducts, setOldProducts, isLoadingOrder };
};